import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowLeft } from "lucide-react";
import { getPost, getPosts } from "@/data/blogPosts";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const post = getPost(lang, slug);
  const fmtDate = (d) =>
    new Date(d).toLocaleDateString(t.locale, { day: "numeric", month: "long", year: "numeric" });

  useEffect(() => {
    if (post) document.title = `${post.title} | RM ${t.footer.brand} - ${t.footer.city}`;
    window.scrollTo(0, 0);
    return () => {
      document.title = "RM Турбо сервиз — Шумен | Ремонт на турбокомпресори";
    };
  }, [post, t]);

  if (!post) {
    return (
      <div className="min-h-screen carbon flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-display font-bold text-4xl uppercase text-white mb-4">{t.blogPost.notFound}</h1>
        <Link to="/" className="text-red-500 font-bold uppercase tracking-wide">{t.blogPost.backHome}</Link>
      </div>
    );
  }

  const others = getPosts(lang).filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="bg-[#0a0a0b] min-h-screen" data-testid="blog-post-page">
      {/* simple header */}
      <header className="bg-[#0a0a0b]/90 backdrop-blur-xl border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="blog-logo-link" className="flex items-center gap-2">
            <span className="bg-red-600 text-white font-display font-bold text-2xl px-2 py-0.5 tracking-tight shadow-[0_0_20px_rgba(239,68,68,0.5)]">RM</span>
            <span className="font-display font-medium uppercase tracking-tight text-white text-xl leading-none">{t.footer.brand}</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageToggle />
            <a
              href="tel:+359892600604"
              className="hidden sm:flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-5 py-2.5 font-bold uppercase tracking-wide text-sm transition-colors"
            >
              <Phone size={16} strokeWidth={2.5} /> {t.nav.call}
            </a>
          </div>
        </div>
      </header>

      <article className="carbon">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <Link to="/#blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-zinc-400 hover:text-red-500 transition-colors text-sm font-semibold uppercase tracking-wide mb-8">
            <ArrowLeft size={16} /> {t.blogPost.back}
          </Link>

          <p className="text-sm font-bold uppercase tracking-[0.25em] text-red-500 mb-4">{fmtDate(post.date)}</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight leading-tight text-white mb-8"
          >
            {post.title}
          </motion.h1>

          <img src={post.image} alt={post.title} className="w-full aspect-[16/9] object-cover border-2 border-white mb-10" />

          <div className="space-y-5">
            {post.body.map((block, i) =>
              block.type === "h" ? (
                <h2 key={i} className="font-display font-bold text-2xl uppercase tracking-tight text-white pt-4">{block.text}</h2>
              ) : (
                <p key={i} className="text-zinc-300 leading-relaxed text-lg">{block.text}</p>
              )
            )}
          </div>

          {/* CTA */}
          <div className="mt-12 bg-red-600 text-white p-8">
            <h3 className="font-display font-bold text-2xl uppercase tracking-tight mb-2">{t.blogPost.ctaTitle}</h3>
            <p className="text-red-100 mb-5">{t.blogPost.ctaBody}</p>
            <a href="tel:+359892600604" className="inline-flex items-center gap-2 bg-white text-red-600 px-6 py-3 font-bold uppercase tracking-wide">
              <Phone size={18} strokeWidth={2.5} /> +359 892 600 604
            </a>
          </div>

          {/* other posts */}
          <div className="mt-16">
            <h3 className="font-display font-bold text-xl uppercase tracking-tight text-white mb-6">{t.blogPost.otherPosts}</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {others.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block bg-zinc-900/40 border border-zinc-800 hover:border-red-500 transition-colors p-5">
                  <p className="font-display font-bold uppercase tracking-tight text-white group-hover:text-red-500 transition-colors leading-tight">{p.title}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
